import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import { getActivationTerms, getTerms, listMyCodes, redeemForDownline } from '../lib/api';
import { prettyRedeemError } from '../lib/redeem-errors';
import { useBrand } from '../storefront/lib/hooks';

/**
 * F4 (extended) — create a slot for ANY downline member (any depth, warm or
 * already-active) using a Code+PIN from the caller's own vault. The recipient
 * is matched by their unique email; the username is an OPTIONAL double-confirm
 * shown only on tenants that collect usernames (`requireUsernameOnSignup`), and
 * the server enforces it only when the recipient's account actually has one.
 * The new slot is owned by them and sponsored by the caller. The slot limit is
 * enforced server-side.
 */
export function RedeemForDownlineDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const brand = useBrand();
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codesQ = useQuery({
    queryKey: ['my-codes', 'available'],
    queryFn: () => listMyCodes({ onlyAvailable: true }),
    enabled: open,
  });
  const termsQ = useQuery({ queryKey: ['activation-terms'], queryFn: getActivationTerms });
  const needsTerms = !!termsQ.data?.required;
  // Pure visibility gate: the field only appears where usernames are collected.
  // It is never required — matching is by email, and plenty of accounts on a
  // username tenant still have none (legacy imports).
  const signupCfgQ = useQuery({ queryKey: ['terms'], queryFn: getTerms, enabled: open });
  const usesUsernames = !!signupCfgQ.data?.requireUsernameOnSignup;

  const codes = useMemo(
    () => (codesQ.data ?? []).filter((c) => c.usageMode !== 'upgrade'),
    [codesQ.data],
  );
  const selected = codes.find((c) => c.id === selectedId) ?? null;

  useEffect(() => {
    if (open) {
      setUsername('');
      setEmail('');
      setSelectedId('');
      setTermsAccepted(false);
      setError(null);
    }
  }, [open]);

  const mut = useMutation({
    meta: { successMessage: 'Slot activated for your affiliate.' },
    mutationFn: () => {
      if (!selected) throw new Error('no_code_selected');
      return redeemForDownline({
        recipientUsername: username.trim() || undefined,
        recipientEmail: email.trim(),
        code: selected.code,
        pin: selected.pin,
        termsAccepted: needsTerms ? termsAccepted : undefined,
        termsVersion: needsTerms ? termsQ.data?.version : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-leads'] });
      qc.invalidateQueries({ queryKey: ['my-codes'] });
      qc.invalidateQueries({ queryKey: ['vault'] });
      qc.invalidateQueries({ queryKey: ['my-slots'] });
      onSuccess();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string } } }).response?.data;
      setError(prettyRedeemError(data?.error ?? 'redeem_failed'));
    },
  });

  const canSubmit = !!selected && email.trim() && !mut.isPending && (!needsTerms || termsAccepted);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a slot for an affiliate</DialogTitle>
          <DialogDescription>
            Spend a Code+PIN from your Vault to create a slot for any of your affiliates, matched by
            their email. They own the slot; you sponsor it. Works for new members and adds another
            slot for already-active ones.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className={`grid gap-3 ${usesUsernames ? 'sm:grid-cols-2' : ''}`}>
            {usesUsernames && (
              <div>
                <label className="text-xs text-muted-foreground">Member username (optional)</label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="their username"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Optional double-check — only enforced if their account has a username.
                </p>
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground">Member email</label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="their@email.com"
              />
            </div>
          </div>

          {codesQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading your codes…</p>
          ) : codes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              You have no available activation codes in your Vault. Buy a {brand.kitLabel} to get
              one.
            </p>
          ) : (
            <div>
              <label className="text-xs text-muted-foreground">Code from your vault</label>
              <select
                className="w-full h-9 rounded-md border bg-background px-2 text-sm"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">Select a code…</option>
                {codes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.productName} · {c.code}
                    {c.slotType === 'free' ? ' (free slot)' : ''}
                  </option>
                ))}
              </select>
              {selected && (
                <div className="mt-2 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                  <div>
                    Code: <span className="font-mono text-foreground">{selected.code}</span>
                  </div>
                  <div>
                    PIN: <span className="font-mono text-foreground">{selected.pin}</span>
                  </div>
                </div>
              )}
              {selected?.slotType === 'free' && (
                <p className="mt-2 text-xs text-amber-600">
                  This is a free slot — placed in the tree but earns no commissions until upgraded.
                </p>
              )}
            </div>
          )}

          {selected && needsTerms && (
            <div className="space-y-2">
              {termsQ.data?.text && (
                <div className="max-h-40 overflow-y-auto rounded-md border bg-background p-2 text-xs whitespace-pre-wrap">
                  {termsQ.data.text}
                </div>
              )}
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I confirm this activation <strong>cannot be changed</strong> once done, and I
                  accept the activation terms above on the member's behalf.
                </span>
              </label>
            </div>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} disabled={!canSubmit}>
            {mut.isPending ? 'Working…' : 'Create slot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
